/**
 * Render a grounded residual system as a Graphviz DAG.
 *
 * The residuals are trees as grounded (see docs/hash-consing.md), so this
 * structurally hash-conses them at render time — identical subexpressions (every
 * occurrence of a variable, each repeated `θ_from − θ_to`) collapse to one node
 * with fan-out edges — turning the forest of trees into one shared DAG. That's
 * both what makes the picture legible and a preview of the hash-consing the
 * evaluator will eventually do.
 *
 * Each residual's root is introduced by a labelled `= 0` node, so the entry
 * points are named; below them, variables are boxes, operators/functions are
 * ellipses, and constants are plain text.
 */

import { RNode, GroundedResidual } from './residual';

const fmtConst = (v: number): string =>
  Number.isInteger(v) ? String(v) : Number(v.toPrecision(4)).toString();

/** Commutative ops can be reordered, so sort their operands to share more. */
function childOrder(op: string, l: number, r: number): [number, number] {
  return op === '+' || op === '*' ? (l <= r ? [l, r] : [r, l]) : [l, r];
}

interface DotNode {
  id: number;
  label: string;
  shape: string;
  fill?: string;
}

/** Build the DOT source for a residual system's shared expression DAG. */
export function residualsToDot(residuals: GroundedResidual[]): string {
  const memo = new Map<RNode, number>(); // per-object, to avoid re-walking
  const byKey = new Map<string, number>(); // structural key → node id
  const nodes: DotNode[] = [];
  const edges: Array<[number, number]> = [];
  let nextId = 0;

  const add = (key: string, make: () => Omit<DotNode, 'id'>, children: number[]): number => {
    const existing = byKey.get(key);
    if (existing !== undefined) return existing;
    const id = nextId++;
    byKey.set(key, id);
    nodes.push({ id, ...make() });
    for (const c of children) edges.push([id, c]);
    return id;
  };

  function intern(n: RNode): number {
    const seen = memo.get(n);
    if (seen !== undefined) return seen;
    let id: number;
    switch (n.k) {
      case 'const':
        id = add(`c:${n.v}`, () => ({ label: fmtConst(n.v), shape: 'plaintext' }), []);
        break;
      case 'var':
        id = add(`v:${n.name}`, () => ({ label: n.name, shape: 'box', fill: '#dbeafe' }), []);
        break;
      case 'neg': {
        const c = intern(n.x);
        id = add(`neg:${c}`, () => ({ label: '−', shape: 'ellipse' }), [c]);
        break;
      }
      case 'fn': {
        const c = intern(n.x);
        id = add(`${n.fn}:${c}`, () => ({ label: n.fn, shape: 'ellipse', fill: '#ede9fe' }), [c]);
        break;
      }
      case 'bin': {
        const [l, r] = childOrder(n.op, intern(n.l), intern(n.r));
        id = add(`${n.op}:${l}:${r}`, () => ({ label: n.op, shape: 'ellipse' }), [l, r]);
        break;
      }
    }
    memo.set(n, id);
    return id;
  }

  // A labelled root per residual: `<name> = 0` → the residual's expression node.
  const roots: string[] = [];
  residuals.forEach((r, i) => {
    const rootId = intern(r.node);
    const label = `${r.name} = 0`.replace(/"/g, '\\"');
    roots.push(`  r${i} [label="${label}", shape=note, style=filled, fillcolor="#fef3c7"];`);
    roots.push(`  r${i} -> n${rootId};`);
  });

  const nodeLines = nodes.map((n) => {
    const label = n.label.replace(/"/g, '\\"');
    const fill = n.fill ? `, style=filled, fillcolor="${n.fill}"` : '';
    return `  n${n.id} [label="${label}", shape=${n.shape}${fill}];`;
  });
  const edgeLines = edges.map(([from, to]) => `  n${from} -> n${to};`);

  return [
    'digraph residuals {',
    '  rankdir=TB;',
    '  node [fontname="monospace", fontsize=10];',
    '  edge [arrowsize=0.6, color="#94a3b8"];',
    ...roots,
    ...nodeLines,
    ...edgeLines,
    '}',
  ].join('\n');
}

/** How many unique DAG nodes the residuals share down to — for the panel header. */
export function residualGraphStats(residuals: GroundedResidual[]): {
  treeNodes: number;
  dagNodes: number;
} {
  const memo = new Map<RNode, number>();
  const byKey = new Map<string, number>();
  let tree = 0;
  let next = 0;
  const walk = (n: RNode): number => {
    const seen = memo.get(n);
    if (seen !== undefined) return seen;
    tree++;
    let key: string;
    switch (n.k) {
      case 'const':
        key = `c:${n.v}`;
        break;
      case 'var':
        key = `v:${n.name}`;
        break;
      case 'neg':
        key = `neg:${walk(n.x)}`;
        break;
      case 'fn':
        key = `${n.fn}:${walk(n.x)}`;
        break;
      case 'bin': {
        const [l, r] = childOrder(n.op, walk(n.l), walk(n.r));
        key = `${n.op}:${l}:${r}`;
        break;
      }
    }
    let id = byKey.get(key);
    if (id === undefined) {
      id = next++;
      byKey.set(key, id);
    }
    memo.set(n, id);
    return id;
  };
  for (const r of residuals) walk(r.node);
  return { treeNodes: tree, dagNodes: next };
}
